import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  AuthAgentResultCode,
  AuthenticationDebug,
  AuthStepStatus,
  AuthStepType,
} from '@/shared/types/tryIt';

import { useAuthScore } from './useAuthScore';
import { selectSelectedTurnAuthentication } from '../../../redux/selectors';
import { TurnAuthentication } from '../../../types/conversation';

const STEP_TRANSLATION_KEY: Partial<Record<AuthStepType, string>> = {
  [AuthStepType.IDENTIFICATION]: 'try_it.auth.identification',
  [AuthStepType.VERIFICATION]: 'try_it.auth.verification',
};

const STATUS_TRANSLATION_KEY: Partial<Record<AuthStepStatus, string>> = {
  [AuthStepStatus.PENDING]: 'try_it.auth.status_pending',
  [AuthStepStatus.IN_PROGRESS]: 'try_it.auth.status_in_progress',
  [AuthStepStatus.COMPLETED]: 'try_it.auth.status_completed',
};

type Flavour = 'primary' | 'successful' | 'warning' | 'destructive' | 'neutral';

const RESULT_BADGE: Partial<Record<AuthAgentResultCode, { key: string; flavour: Flavour }>> = {
  [AuthAgentResultCode.SUCCESS]: { key: 'try_it.auth.result.success', flavour: 'successful' },
  [AuthAgentResultCode.FAILURE]: { key: 'try_it.auth.result.failure', flavour: 'warning' },
  [AuthAgentResultCode.SYSTEM_FAILURE]: {
    key: 'try_it.auth.result.failed',
    flavour: 'destructive',
  },
  [AuthAgentResultCode.SKIPPED]: { key: 'try_it.auth.result.skipped', flavour: 'neutral' },
};

type SectionProps = { title: string; className?: string; children: ReactNode };

const FLAVOUR_CLASS: Record<Flavour, string> = {
  primary: 'border-primary text-primary',
  neutral: 'border-border text-foreground',
  successful: 'border-success text-success',
  warning: 'border-warning text-warning',
  destructive: 'border-destructive text-destructive',
};

const AuthBadge = ({ flavour, children }: { flavour: Flavour; children: ReactNode }) => (
  <Badge
    type="outline"
    rounded={false}
    className={cn('bg-background leading-4 font-semibold', FLAVOUR_CLASS[flavour])}
  >
    {children}
  </Badge>
);

const Section = ({ title, className, children }: SectionProps) => (
  <section className={cn('flex flex-col items-start gap-2 self-stretch', className)}>
    <h3 className="text-sm leading-5 font-medium text-foreground">{title}</h3>
    {children}
  </section>
);

type RowProps = { label: string; className?: string; children: ReactNode };

const Row = ({ label, className, children }: RowProps) => (
  <div
    className={cn(
      'flex w-full items-center gap-2 rounded-lg border border-border bg-muted p-2.5',
      className
    )}
  >
    <span className="flex-1 text-sm leading-none font-medium text-muted-foreground">{label}</span>
    {children}
  </div>
);

const ValueRow = ({ label, value }: { label: string; value: number }) => (
  <Row label={label} className="h-11.5">
    <span className="text-sm leading-none font-medium text-foreground">{value}</span>
  </Row>
);

const resultBadge = (auth: AuthenticationDebug) => {
  const resolved = auth.final_result_code && RESULT_BADGE[auth.final_result_code];
  if (resolved) {
    return resolved;
  }
  const key = auth.status && STATUS_TRANSLATION_KEY[auth.status];
  return key ? { key, flavour: 'neutral' as Flavour } : undefined;
};

const NoAuthentication = () => {
  const { t } = useTranslation();

  return (
    <div className="text-sm leading-5 text-muted-foreground">{t('try_it.auth.empty_state')}</div>
  );
};

const AuthenticationState = ({ auth }: { auth: TurnAuthentication }) => {
  const { t } = useTranslation();
  const score = useAuthScore(auth);

  const stepKey = auth.current_step ? STEP_TRANSLATION_KEY[auth.current_step] : undefined;
  const result = resultBadge(auth);
  const validatedKeys = auth.validated_question_keys ?? [];

  return (
    <div className="flex w-full flex-col gap-6">
      {(stepKey || result) && (
        <Section title={t('try_it.auth.state')}>
          {stepKey && (
            <Row label={t('try_it.auth.current_step')}>
              <AuthBadge flavour="primary">{t(stepKey)}</AuthBadge>
            </Row>
          )}
          {result && (
            <Row label={t('try_it.auth.result_label')}>
              <AuthBadge flavour={result.flavour}>{t(result.key)}</AuthBadge>
            </Row>
          )}
        </Section>
      )}

      <Section title={t('try_it.auth.progress')}>
        {score !== undefined && <ValueRow label={t('try_it.auth.score')} value={score} />}
        <ValueRow label={t('try_it.auth.attempts')} value={auth.failed_attempts ?? 0} />
        <ValueRow label={t('try_it.auth.system_errors')} value={auth.system_errors ?? 0} />
      </Section>

      <Section title={t('try_it.auth.validated_question_keys')} className="justify-center gap-1.5">
        {validatedKeys.length === 0 ? (
          <span className="self-stretch text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.auth.validated_question_keys_empty')}
          </span>
        ) : (
          <div className="flex flex-wrap items-start gap-2">
            {validatedKeys.map((key) => (
              <AuthBadge key={key} flavour="successful">
                {key}
              </AuthBadge>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export const AuthenticationPanel = () => {
  const auth = useSelector(selectSelectedTurnAuthentication);

  return auth ? <AuthenticationState auth={auth} /> : <NoAuthentication />;
};
